import { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom-v5-compat';

import { ONBOARDING_STAGE } from 'src/data/onboarding';
import { getQueryParamValue } from 'src/lib/browser';
import { stripGraphQLErrorPrefix } from 'src/lib/graphql';
import * as gtm from 'src/lib/gtm';
import { useEffectOnce } from 'src/lib/hooks';
import { isMobileDevice } from 'src/lib/userAgent';
import { useNavigateToApplication } from 'src/services/useNavigateToApplication';
import { useUserActions } from 'src/store';

export const useViewModel = () => {
  const userActions = useUserActions();
  const navigateToApplication = useNavigateToApplication();
  const location = useLocation();

  // Form state
  const [registerError, setRegisterError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Check login state on page load
  useEffectOnce(() => {
    setLoading(true);

    userActions
      .verifyAuthentication()
      .then(() => {
        setLoading(false);
        navigateToApplication();
      })
      .catch(() => {
        setLoading(false);
      });
  });

  // Clear registration error on any form change
  const handleFormChange = useCallback(() => {
    setRegisterError(null);
  }, []);

  // Run authentication
  const handleRegister = useCallback(
    (email, password) => {
      setSubmitting(true);
      setRegisterError(null);

      const inviteToken = getQueryParamValue('invite');

      userActions
        .register(email.trim(), password.trim(), inviteToken)
        .then((currentUser) => {
          gtm.pushDataLayerEvent(gtm.EVENTS.userSignUp, {
            mobile: isMobileDevice(),
            email: currentUser.email,
          });

          userActions.updateOnboardingStageValue(
            ONBOARDING_STAGE.checkoutPlanRoute,
            location?.state?.from
          );

          setSubmitting(false);
          navigateToApplication();
        })
        .catch((error) => {
          setSubmitting(false);
          // TODO: need to verify if this is a graphql error and not some generic error.
          const message = stripGraphQLErrorPrefix(error);
          setRegisterError(message);
        });
    },
    [userActions, location, navigateToApplication]
  );

  /**
   * Handle start of oauth process
   */
  const handleOAuthStart = useCallback(() => {
    setRegisterError(null);
  }, []);

  const handleOAuthSuccess = useCallback(
    (result) => {
      userActions
        .loginOAuth(result)
        .then(() => {
          navigateToApplication({ addFrom: true, wasUserCreated: result.wasUserCreated });
        })
        .catch((loginError) => {
          const message = stripGraphQLErrorPrefix(loginError);
          setRegisterError(message);
        });
    },
    [navigateToApplication, userActions]
  );

  const handleOAuthError = useCallback((error) => {
    setRegisterError(error ? error.message : error);
  }, []);

  return {
    loading,
    submitting,
    registerError,
    handleFormChange,
    handleRegister,
    handleOAuthStart,
    handleOAuthSuccess,
    handleOAuthError,
  };
};
